/**
 * Fetch wrapper with automatic token refresh
 *
 * Automatically refreshes access token when it expires (401 error)
 * and retries the original request. Only forces re-login if refresh
 * token is also expired.
 */

import { getCSRFToken } from './csrf'

// Get API base URL from environment variable
// In development: uses relative URLs (proxied by React dev server)
// In production: uses full API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Convert relative URLs to absolute URLs using API_BASE_URL
 */
function getFullUrl(input: RequestInfo | URL): string {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
}

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers when token is refreshed
 */
function onRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

/**
 * Get refresh CSRF token from cookies
 */
function getRefreshCSRFToken(): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; csrf_refresh_token=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<boolean> {
  try {
    // For refresh endpoint, we need the refresh CSRF token, not access CSRF token
    const refreshCSRFToken = getRefreshCSRFToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (refreshCSRFToken) {
      headers['X-CSRF-TOKEN'] = refreshCSRFToken;
    }

    const response = await fetch(getFullUrl('/api/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers
    });

    if (response.ok) {
      // Wait a tiny bit for browser to process the Set-Cookie headers
      // This ensures cookies are available for the retry
      await new Promise(resolve => setTimeout(resolve, 50));

      return true;
    }

    // Refresh token is also expired or invalid
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

/**
 * Fetch with automatic token refresh
 *
 * @param input - URL or Request object
 * @param init - RequestInit options
 * @returns Promise<Response>
 */
export async function fetchWithRefresh(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Convert to full URL if needed
  const fullUrl = getFullUrl(input);

  // Make the initial request
  const response = await fetch(fullUrl, init);

  // If not a 401, return the response as-is
  if (response.status !== 401) {
    return response;
  }

  // Check if this is an auth endpoint that should NOT trigger refresh
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  if (url.includes('/api/auth/login') ||
      url.includes('/api/auth/register') ||
      url.includes('/api/auth/refresh')) {
    return response;
  }

  // If we're already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeTokenRefresh(async () => {
        // Retry the original request after token refresh
        // Update CSRF token in headers if it exists
        const updatedInit = { ...init };
        if (updatedInit.headers) {
          const headersObj = new Headers(updatedInit.headers);
          const newCSRFToken = getCSRFToken();
          if (newCSRFToken) {
            headersObj.set('X-CSRF-TOKEN', newCSRFToken);
          }
          updatedInit.headers = headersObj;
        }

        const retryResponse = await fetch(fullUrl, updatedInit);
        resolve(retryResponse);
      });
    });
  }

  // Set the refreshing flag
  isRefreshing = true;

  try {
    // Attempt to refresh the token
    const refreshSuccess = await refreshAccessToken();

    if (refreshSuccess) {
      // Token refreshed successfully, notify subscribers
      onRefreshed('refreshed');

      // Retry the original request with updated CSRF token
      // Update CSRF token in headers if it exists
      const updatedInit = { ...init };
      if (updatedInit.headers) {
        const headersObj = new Headers(updatedInit.headers);
        const newCSRFToken = getCSRFToken();
        if (newCSRFToken) {
          headersObj.set('X-CSRF-TOKEN', newCSRFToken);
        }
        updatedInit.headers = headersObj;
      }

      const retryResponse = await fetch(fullUrl, updatedInit);
      return retryResponse;
    } else {
      // Refresh token also expired, redirect to login
      // Clear any auth state and redirect
      // Only redirect if this is not /api/auth/me (to prevent redirect on initial page load)
      if (!url.includes('/api/auth/me')) {
        window.location.href = '/login?session_expired=true';
      }

      // Return the original 401 response
      return response;
    }
  } finally {
    isRefreshing = false;
  }
}

/**
 * Helper function to check if we should use fetchWithRefresh
 * Use for authenticated API calls only
 */
export function shouldUseRefresh(url: string): boolean {
  // Don't use refresh for auth endpoints
  if (url.includes('/api/auth/login') ||
      url.includes('/api/auth/register') ||
      url.includes('/api/auth/refresh')) {
    return false;
  }

  // Use refresh for all other API calls
  return url.startsWith('/api/');
}
